import { useEffect, useRef, useCallback } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * useRealtimeOrders - WebSocket temps réel pour les commandes.
 *
 * Optimisations pour minimiser le lag de la sirène d'auto-dispatch :
 *  • Reconnexion exponentielle (3s → 6s → 12s → max 30s) avec reset au succès.
 *  • Ping rapide 15s (vs proxy timeout ~60s) — détecte coupure ~3× plus vite.
 *  • `visibilitychange` : reconnect immédiat quand l'onglet redevient visible.
 *  • `online` event : reconnect immédiat quand la connexion réseau revient.
 *  • Référence callback stable (pas de reconnexion sur changement de handler).
 */
export default function useRealtimeOrders(userId, onEvent) {
  const wsRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const onEventRef = useRef(onEvent);
  const shouldReconnectRef = useRef(true);
  const reconnectAttemptRef = useRef(0);

  useEffect(() => {
    onEventRef.current = onEvent;
  }, [onEvent]);

  const connect = useCallback(() => {
    if (!userId || !BACKEND_URL) return;
    // Évite double ouverture si déjà connecté
    if (wsRef.current && wsRef.current.readyState <= 1) return;

    const wsProtocol = BACKEND_URL.startsWith("https://") ? "wss://" : "ws://";
    const host = BACKEND_URL.replace(/^https?:\/\//, "");
    const url = `${wsProtocol}${host}/api/ws/user/${userId}`;

    try {
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        // Reset backoff au succès
        reconnectAttemptRef.current = 0;
        // Ping plus fréquent (15s) — détecte coupure plus tôt = sirène plus rapide
        pingIntervalRef.current = setInterval(() => {
          try {
            if (ws.readyState === WebSocket.OPEN) ws.send("ping");
          } catch (err) {
            console.debug("[WS] ping send failed:", err);
          }
        }, 15000);
      };

      ws.onmessage = (e) => {
        if (e.data === "pong") return;
        try {
          const { event, data } = JSON.parse(e.data);
          if (onEventRef.current) onEventRef.current(event, data);
        } catch (err) {
          console.debug("[WS] message parse error:", err);
        }
      };

      ws.onclose = () => {
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }
        if (shouldReconnectRef.current) {
          // Backoff exponentiel cappé 30s : 3s, 6s, 12s, 24s, 30s, 30s…
          const attempt = reconnectAttemptRef.current;
          const delay = Math.min(3000 * Math.pow(2, attempt), 30000);
          reconnectAttemptRef.current = attempt + 1;
          reconnectTimeoutRef.current = setTimeout(connect, delay);
        }
      };

      ws.onerror = () => {
        try { ws.close(); } catch (err) { console.debug("[WS] close on error failed:", err); }
      };
    } catch (e) {
      console.debug("[WS] construction failed, retry in 3s:", e);
      reconnectTimeoutRef.current = setTimeout(connect, 3000);
    }
  }, [userId]);

  useEffect(() => {
    shouldReconnectRef.current = true;
    connect();

    // Reconnect immédiat quand l'onglet redevient visible (mobile screen unlock,
    // tab switch) — la sirène se déclenche dès retour à l'écran.
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        const ws = wsRef.current;
        if (!ws || ws.readyState >= 2) {
          reconnectAttemptRef.current = 0;
          if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
          connect();
        }
      }
    };
    // Reconnect immédiat quand le réseau revient (4G → WiFi, retour offline→online).
    const onOnline = () => {
      reconnectAttemptRef.current = 0;
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      connect();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("online", onOnline);

    return () => {
      shouldReconnectRef.current = false;
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("online", onOnline);
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      if (pingIntervalRef.current) clearInterval(pingIntervalRef.current);
      if (wsRef.current) {
        try { wsRef.current.close(); } catch (err) { console.debug("[WS] cleanup close failed:", err); }
      }
    };
  }, [connect]);
}
